import { supabase } from '../config/supabase'
import secureLogger from '../utils/secureLogger'
import { clearGoogleAuth } from './googleTokenService'

// Global cache to prevent duplicate calls across all instances
const GLOBAL_USAGE_CACHE = {
  data: null,
  timestamp: null,
  pendingPromise: null // Prevent multiple simultaneous calls
};

class UserService {
  constructor() {
    this.currentUser = null
    this.sessionToken = null
    this.CACHE_TTL = 600000 // 10 minutes cache - only refresh when data changes
    this._syncInFlight = null
    this._syncedAccessToken = null
  }

  // Intentionally no-op: currentUser is seeded only by syncFromAuthSession once
  // the Supabase session is confirmed. Seeding from localStorage pre-auth caused
  // a race on OAuth redirect where queries went out before the JWT was ready,
  // producing 401/406 from PostgREST and 429 on refresh_token retries.
  initializeFromStorage() {}

  // Clear usage counts cache (call when data changes)
  clearUsageCountsCache() {
    GLOBAL_USAGE_CACHE.data = null
    GLOBAL_USAGE_CACHE.timestamp = null
    GLOBAL_USAGE_CACHE.pendingPromise = null
    console.log('🔄 Cache cleared - will refresh on next access');
  }

  // Get or generate user avatar with persistence
  getUserAvatar(email, forceRegenerate = false) {
    const avatarKey = `logislip_avatar_${email}`;

    // Check if we have a stored avatar (unless forcing regeneration)
    if (!forceRegenerate) {
      const storedAvatar = localStorage.getItem(avatarKey);
      if (storedAvatar) {
        return storedAvatar;
      }
    }

    // Generate new avatar
    const newAvatar = this.generateRandomAvatar(email, forceRegenerate);

    // Store it persistently
    localStorage.setItem(avatarKey, newAvatar);

    return newAvatar;
  }

  // Generate random avatar URL using DiceBear API
  generateRandomAvatar(seed, forceNew = false) {
    // Available avatar styles - choosing ones that work well for profile pictures
    const avatarStyles = [
      'avataaars',       // Human-like avatars
      'adventurer',      // Stylish human avatars
      'lorelei',         // Modern minimal avatars
      'personas',        // Professional avatars
      'notionists',      // Clean modern style
      'open-peeps'       // Hand-drawn style
    ];

    // If forcing new avatar, add timestamp to seed
    const avatarSeed = forceNew ? `${seed}-${Date.now()}` : seed;

    // Pick a random style or use user preference
    const randomStyle = avatarStyles[Math.floor(Math.random() * avatarStyles.length)];

    // Use email or user ID as seed to ensure consistency
    const encodedSeed = encodeURIComponent(avatarSeed);

    // Return DiceBear API URL - using SVG for better quality and smaller size
    return `https://api.dicebear.com/9.x/${randomStyle}/svg?seed=${encodedSeed}&size=128`;
  }

  // Get user profile from Google OAuth
  async getUserProfile(accessToken) {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user profile')
      }

      return await response.json()
    } catch (error) {
      secureLogger.error('Error fetching user profile:', error)
      throw error
    }
  }

  async findLegacyUserRecord(profileData) {
    const email = profileData.email?.trim()
    if (email) {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single()

      if (!error) {
        return data
      }

      if (error.code && error.code !== 'PGRST116') {
        throw error
      }
    }

    if (profileData.googleId) {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('google_id', profileData.googleId)
        .single()

      if (!error) {
        return data
      }

      if (error.code && error.code !== 'PGRST116') {
        throw error
      }
    }

    return null
  }

  // Sync Supabase Auth session with public users table
  async syncFromAuthSession(session) {
    if (!session?.user) {
      this._syncInFlight = null;
      this._syncedAccessToken = null;
      await this.logout();
      return null;
    }

    // Dedupe concurrent calls for the same access_token. On OAuth redirect the
    // client emits INITIAL_SESSION + SIGNED_IN (and sometimes TOKEN_REFRESHED)
    // in quick succession; without this guard each fires a parallel GET/UPDATE
    // on users and an INSERT on usage_logs, which is what triggers the refresh
    // rate-limit cascade.
    if (this._syncInFlight && this._syncedAccessToken === session.access_token) {
      return this._syncInFlight;
    }

    this._syncedAccessToken = session.access_token;
    this._syncInFlight = this._runSyncFromAuthSession(session)
      .finally(() => { this._syncInFlight = null; });
    return this._syncInFlight;
  }

  async _runSyncFromAuthSession(session) {
    try {
      this.sessionToken = session.access_token;
      const authUser = session.user;
      const profileData = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email || 'User',
        picture: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture,
        googleId: authUser.user_metadata?.sub || authUser.id,
      };

      const { data: existingUser, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      let userRecord = existingUser;
      if (!userRecord) {
        userRecord = await this.findLegacyUserRecord(profileData);
      }

      const avatarUrl = profileData.picture || userRecord?.avatar_url || this.getUserAvatar(profileData.email);
      const nowIso = new Date().toISOString();
      let createdNewUser = false;

      if (!userRecord) {
        // Determine user role based on email
        const isAdminEmail = ['admin@logislip.com', 'support@logislip.com'].includes(profileData.email.toLowerCase())
        const userRole = isAdminEmail ? 'admin' : 'user'
        
        const { data: newUser, error: createError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            owner_id: authUser.id,
            google_id: profileData.googleId,
            email: profileData.email,
            name: profileData.name,
            avatar_url: avatarUrl,
            role: userRole,
            subscription_tier: 'free',
            subscription_status: 'active',
            usage_count: 0,
            usage_limit: 3,
            last_login: nowIso,
            updated_at: nowIso,
          })
          .select()
          .single();

        if (createError) throw createError;
        userRecord = newUser;
        createdNewUser = true;
      } else {
        // Update role if user email is now in admin list and they don't have admin role
        const isAdminEmail = ['admin@logislip.com', 'support@logislip.com'].includes(profileData.email.toLowerCase())
        const shouldBeAdmin = isAdminEmail && userRecord.role !== 'admin'
        const newRole = shouldBeAdmin ? 'admin' : userRecord.role || 'user'
        
        const { data: updatedUser, error: updateError } = await supabase
          .from('users')
          .update({
            owner_id: userRecord.owner_id || authUser.id,
            google_id: profileData.googleId || userRecord.google_id,
            email: profileData.email,
            name: profileData.name,
            avatar_url: avatarUrl,
            role: newRole,
            last_login: nowIso,
            updated_at: nowIso,
          })
          .eq('id', userRecord.id)
          .select()
          .single();

        if (updateError) throw updateError;
        userRecord = updatedUser;
      }

      if (createdNewUser) {
        await this.initializeResourceUsage(userRecord.id, userRecord.subscription_tier);
      }

      this.currentUser = { ...userRecord };
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser));

      await this.logUserAction('login', {
        email: profileData.email,
        login_time: new Date().toISOString(),
      }, 'authentication', userRecord.id);

      return this.currentUser;
    } catch (error) {
      secureLogger.error('Authentication sync error:', error);
      throw error;
    }
  }

  // Update user profile fields (e.g., phone)
  async updateUserProfile(update) {
    if (!this.currentUser) throw new Error('Not authenticated')
    try {
      const { data: updated, error } = await supabase
        .from('users')
        .update(update)
        .eq('id', this.currentUser.id)
        .select()
        .single()
      if (error) throw error
      this.currentUser = updated
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser))
      return updated
    } catch (err) {
      secureLogger.error('Failed to update user profile:', err)
      throw err
    }
  }

  // Check if user session is valid using Supabase Auth
  async validateSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session) {
        // Try to recover from local storage if supabase client lost state but we have token?
        // Usually supabase client persists it.
        this.logout();
        return false;
      }

      // Check expiration
      const expiresAt = new Date(session.expires_at * 1000); // JWT exp is seconds
      if (expiresAt < new Date()) {
        this.logout();
        return false;
      }

      // Refresh our local token reference
      this.sessionToken = session.access_token;
      return true;

    } catch (error) {
      secureLogger.error('❌ Session validation error:', error)
      this.logout()
      return false
    }
  }

  // Log user actions for tracking
  async logUserAction(action, details = {}, resourceType = 'general', resourceId = null) {
    if (!this.currentUser) return

    try {
      await supabase
        .from('usage_logs')
        .insert({
          user_id: this.currentUser.id,
          action,
          resource_type: resourceType,
          resource_id: resourceId,
          details
        })
    } catch (error) {
      secureLogger.error('Error logging user action:', error)
    }
  }

  // Check usage limits
  async checkUsageLimit(action = 'invoice_create') {
    if (!this.currentUser) return false

    try {
      // Get current user data
      const { data: user, error } = await supabase
        .from('users')
        .select('usage_count, usage_limit, subscription_tier')
        .eq('id', this.currentUser.id)
        .single()

      if (error) throw error

      // Update local user data
      this.currentUser = { ...this.currentUser, ...user }
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser))

      return user.usage_count < user.usage_limit
    } catch (error) {
      secureLogger.error('Error checking usage limit:', error)
      throw error
    }
  }

  // Increment usage count
  async incrementUsage(action = 'invoice_create') {
    if (!this.currentUser) return

    try {
      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({
          usage_count: this.currentUser.usage_count + 1
        })
        .eq('id', this.currentUser.id)
        .select()
        .single()

      if (error) throw error

      this.currentUser = updatedUser
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser))

      // Log the usage
      await this.logUserAction(action, {
        new_usage_count: updatedUser.usage_count,
        usage_limit: updatedUser.usage_limit
      }, 'usage', this.currentUser.id)

      return updatedUser
    } catch (error) {
      secureLogger.error('Error incrementing usage:', error)
      throw error
    }
  }

  // Initialize resource usage for a new user
  async initializeResourceUsage(userId, subscriptionTier = 'free') {
    try {
      const plans = await this.getSubscriptionPlans()
      const currentPlan = plans.find(plan => plan.plan_id === subscriptionTier) ||
        plans.find(plan => plan.plan_id === 'free')

      const resourceTypes = ['clients', 'products', 'invoices', 'invoice_exports', 'email_shares']
      const resourceLimits = {
        clients: currentPlan?.limitations?.clients || 3,
        products: currentPlan?.limitations?.products || 3,
        invoices: currentPlan?.limitations?.invoicesSaveExport || 3,
        invoice_exports: currentPlan?.limitations?.invoicesSaveExport || 3,
        email_shares: currentPlan?.limitations?.invoicesSaveExport || 3
      }

      for (const resourceType of resourceTypes) {
        await supabase
          .from('resource_usage')
          .upsert({
            user_id: userId,
            resource_type: resourceType,
            current_count: 0,
            limit_count: resourceLimits[resourceType],
            last_reset: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,resource_type'
          })
      }
    } catch (error) {
      secureLogger.error('Error initializing resource usage:', error)
      throw error
    }
  }

  // Get resource usage counts from Supabase with global caching and deduplication
  async getResourceUsageCounts(useCache = true) {
    if (!this.currentUser) return {}

    // Check global cache first if enabled
    if (useCache && GLOBAL_USAGE_CACHE.data && GLOBAL_USAGE_CACHE.timestamp) {
      const cacheAge = Date.now() - GLOBAL_USAGE_CACHE.timestamp
      if (cacheAge < this.CACHE_TTL) {
        // Return cached data silently
        return GLOBAL_USAGE_CACHE.data
      }
    }

    // If there's already a pending promise, wait for it instead of making a new call
    if (GLOBAL_USAGE_CACHE.pendingPromise) {
      try {
        return await GLOBAL_USAGE_CACHE.pendingPromise
      } catch (error) {
        // If the pending promise failed, we'll try again below
        GLOBAL_USAGE_CACHE.pendingPromise = null
      }
    }

    // Create the fetch promise and store it to prevent duplicate calls
    GLOBAL_USAGE_CACHE.pendingPromise = this._fetchUsageCountsFromSupabase()

    try {
      const result = await GLOBAL_USAGE_CACHE.pendingPromise
      return result
    } catch (error) {
      GLOBAL_USAGE_CACHE.pendingPromise = null
      throw error
    } finally {
      GLOBAL_USAGE_CACHE.pendingPromise = null
    }
  }

  // Private method to actually fetch from Supabase
  async _fetchUsageCountsFromSupabase() {
    try {
      // Only log the first fetch
      const isFirstFetch = !GLOBAL_USAGE_CACHE.data
      if (isFirstFetch) {
        console.log('⚡ Fetching usage counts from Supabase...')
      }

      // Add timeout to Supabase call
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Supabase timeout after 3 seconds')), 3000)
      );

      const supabasePromise = supabase
        .from('resource_usage')
        .select('resource_type, current_count, limit_count')
        .eq('user_id', this.currentUser.id);

      const { data: usage, error } = await Promise.race([supabasePromise, timeoutPromise]);

      if (error) throw error

      // Convert array to object
      const usageCounts = {}
      if (usage && Array.isArray(usage)) {
        usage.forEach(item => {
          usageCounts[item.resource_type] = item.current_count
        })
      }

      // Cache the results globally
      GLOBAL_USAGE_CACHE.data = usageCounts
      GLOBAL_USAGE_CACHE.timestamp = Date.now()

      if (isFirstFetch) {
        console.log('📊 Usage counts cached:', usageCounts)
      }

      return usageCounts
    } catch (error) {
      // Fallback: return cached data if available, or safe defaults
      if (GLOBAL_USAGE_CACHE.data) {
        return GLOBAL_USAGE_CACHE.data
      }

      // Safe defaults - no logging to reduce spam
      return {
        clients: 0,
        products: 0,
        invoices: 0,
        invoice_exports: 0,
        email_shares: 0
      }
    }
  }

  // Check resource usage limit
  async checkResourceUsageLimit(resourceType) {
    if (!this.currentUser) return false

    try {
      const { data: usage, error } = await supabase
        .from('resource_usage')
        .select('current_count, limit_count')
        .eq('user_id', this.currentUser.id)
        .eq('resource_type', resourceType)
        .single()

      if (error) throw error

      return usage.current_count < usage.limit_count
    } catch (error) {
      secureLogger.error('Error checking resource usage limit:', error)
      throw error
    }
  }

  // Increment resource usage count
  async incrementResourceUsage(resourceType, resourceId = null) {
    if (!this.currentUser) return

    try {
      // First get the current count
      let { data: currentUsage, error: fetchError } = await supabase
        .from('resource_usage')
        .select('current_count, limit_count')
        .eq('user_id', this.currentUser.id)
        .eq('resource_type', resourceType)
        .single()

      if (fetchError) {
        // If resource usage doesn't exist, initialize it first
        if (fetchError.code === 'PGRST116') {
          console.log(`Initializing resource usage for ${resourceType}`);
          await this.initializeResourceUsage(this.currentUser.id, this.currentUser.subscription_tier);
          // Try to fetch again
          const { data: newUsage, error: refetchError } = await supabase
            .from('resource_usage')
            .select('current_count, limit_count')
            .eq('user_id', this.currentUser.id)
            .eq('resource_type', resourceType)
            .single()

          if (refetchError) throw refetchError;
          currentUsage = newUsage;
        } else {
          throw fetchError;
        }
      }

      // Update with incremented count
      const newCount = (currentUsage.current_count || 0) + 1;
      const { data: updatedUsage, error } = await supabase
        .from('resource_usage')
        .update({
          current_count: newCount,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', this.currentUser.id)
        .eq('resource_type', resourceType)
        .select()
        .single()

      if (error) throw error

      console.log(`✅ ${resourceType} count: ${currentUsage.current_count} → ${updatedUsage.current_count}`);

      // Invalidate cache after increment to force fresh data on next check
      this.clearUsageCountsCache();

      // Log the usage
      await this.logUserAction(`${resourceType}_create`, {
        new_count: updatedUsage.current_count,
        limit: updatedUsage.limit_count
      }, resourceType, resourceId)

      return updatedUsage
    } catch (error) {
      secureLogger.error('Error incrementing resource usage:', error)
      console.error('Full error details:', error);
      throw error
    }
  }

  // Decrement resource usage count
  async decrementResourceUsage(resourceType, resourceId = null) {
    if (!this.currentUser) return

    try {
      // First get the current count
      const { data: currentUsage, error: fetchError } = await supabase
        .from('resource_usage')
        .select('current_count, limit_count')
        .eq('user_id', this.currentUser.id)
        .eq('resource_type', resourceType)
        .single()

      if (fetchError) throw fetchError

      // Calculate new count (prevent negative)
      const newCount = Math.max((currentUsage.current_count || 0) - 1, 0);

      // Update with decremented count
      const { data: updatedUsage, error } = await supabase
        .from('resource_usage')
        .update({
          current_count: newCount,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', this.currentUser.id)
        .eq('resource_type', resourceType)
        .select()
        .single()

      if (error) throw error

      console.log(`✅ ${resourceType} count decremented from ${currentUsage.current_count} to ${updatedUsage.current_count}`);

      // Log the usage
      await this.logUserAction(`${resourceType}_delete`, {
        new_count: updatedUsage.current_count,
        limit: updatedUsage.limit_count
      }, resourceType, resourceId)

      return updatedUsage
    } catch (error) {
      secureLogger.error('Error decrementing resource usage:', error)
      console.error('Full error details:', error);
      throw error
    }
  }



  // Update resource usage limits when subscription changes
  async updateResourceUsageLimits(subscriptionTier) {
    if (!this.currentUser) return

    try {
      const plans = await this.getSubscriptionPlans()
      const currentPlan = plans.find(plan => plan.plan_id === subscriptionTier) ||
        plans.find(plan => plan.plan_id === 'free')

      const resourceLimits = {
        clients: currentPlan?.limitations?.clients || 3,
        products: currentPlan?.limitations?.products || 3,
        invoices: currentPlan?.limitations?.invoicesSaveExport || 3,
        invoice_exports: currentPlan?.limitations?.invoicesSaveExport || 3,
        email_shares: currentPlan?.limitations?.invoicesSaveExport || 3
      }

      for (const [resourceType, limit] of Object.entries(resourceLimits)) {
        await supabase
          .from('resource_usage')
          .update({
            limit_count: limit,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', this.currentUser.id)
          .eq('resource_type', resourceType)
      }
    } catch (error) {
      secureLogger.error('Error updating resource usage limits:', error)
      throw error
    }
  }

  // Get subscription plans
  async getSubscriptionPlans() {
    try {
      const { data: plans, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('active', true)
        .order('price', { ascending: true })

      if (error) throw error
      return plans
    } catch (error) {
      secureLogger.error('Error fetching subscription plans:', error)
      return []
    }
  }

  // Update user subscription
  async updateSubscription(subscriptionData) {
    if (!this.currentUser) return

    try {
      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({
          subscription_tier: subscriptionData.tier,
          subscription_status: subscriptionData.status,
          subscription_end_date: subscriptionData.endDate,
          usage_limit: subscriptionData.usageLimit,
          usage_count: 0 // Reset usage count on subscription change
        })
        .eq('id', this.currentUser.id)
        .select()
        .single()

      if (error) throw error

      // Update resource usage limits for the new subscription tier
      await this.updateResourceUsageLimits(subscriptionData.tier)

      this.currentUser = updatedUser
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser))

      await this.logUserAction('subscription_updated', subscriptionData, 'subscription', this.currentUser.id)

      return updatedUser
    } catch (error) {
      secureLogger.error('Error updating subscription:', error)
      throw error
    }
  }

  // Get user analytics
  async getUserAnalytics() {
    if (!this.currentUser) return null

    try {
      const { data: logs, error } = await supabase
        .from('usage_logs')
        .select('*')
        .eq('user_id', this.currentUser.id)
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error

      // Process analytics data
      const analytics = {
        totalActions: logs.length,
        recentActions: logs.slice(0, 10),
        actionCounts: logs.reduce((acc, log) => {
          acc[log.action] = (acc[log.action] || 0) + 1
          return acc
        }, {}),
        lastActivity: logs[0]?.created_at
      }

      return analytics
    } catch (error) {
      secureLogger.error('Error fetching user analytics:', error)
      return null
    }
  }

  // Debug function to check avatar storage
  debugAvatarStorage() {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('logislip_avatar_')) {
        // Avatar storage check (for debugging)
      }
    }
  }

  // Production-safe logout that bypasses problematic Supabase logout
  async logout() {
    // Clear local session immediately - this is all we really need
    this.clearLocalSession()
    
    // Don't attempt Supabase logout at all to avoid 403 errors
    // The session will expire naturally on the server
    
    // Clear all possible authentication-related storage
    try {
      // Clear localStorage items
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (
          key.includes('supabase') || 
          key.includes('auth') || 
          key.includes('session') ||
          key.includes('sb-') ||
          key.startsWith('logislip_')
        )) {
          // Keep avatar storage
          if (!key.includes('avatar')) {
            keysToRemove.push(key)
          }
        }
      }
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key)
        } catch (e) {
          // Ignore removal errors
        }
      })
      
      // Clear session storage completely
      sessionStorage.clear()
      
      // Clear any authentication cookies
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=")
        const name = eqPos > -1 ? c.substr(0, eqPos) : c
        const trimmedName = name.trim()
        if (trimmedName.includes('supabase') || trimmedName.includes('auth') || trimmedName.includes('sb-')) {
          document.cookie = `${trimmedName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
          document.cookie = `${trimmedName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        }
      })
    } catch (error) {
      console.warn('Error clearing storage:', error)
    }

    // Note: We intentionally keep avatar storage (logislip_avatar_*) 
    // so users don't lose their generated avatars between sessions
  }

  // Force logout when regular logout fails - emergency fallback
  forceLogout() {
    try {
      // Nuclear option - clear everything and force navigation
      this.clearLocalSession()
      
      // Clear all storage
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch (e) {
        // Ignore errors
      }
      
      // Force navigation with replace to prevent back button
      window.location.replace('/')
    } catch (error) {
      // Ultimate fallback - reload the page
      try {
        window.location.href = '/'
      } catch (e) {
        window.location.reload()
      }
    }
  }

  clearLocalSession() {
    // Clear local data but preserve avatar storage
    this.currentUser = null
    this.sessionToken = null
    
    try {
      localStorage.removeItem('logislip_user')
      // Also clear any Supabase specific keys that might persist
      const supabaseKeys = ['sb-nulnxemirpwcaikajiwz-auth-token', 'supabase.auth.token']
      supabaseKeys.forEach(key => {
        try {
          localStorage.removeItem(key)
        } catch (e) {
          // Ignore errors
        }
      })
    } catch (error) {
      console.warn('Error clearing localStorage:', error)
    }
    
    try {
      clearGoogleAuth()
    } catch (error) {
      console.warn('Error clearing Google auth:', error)  
    }
  }

  // Global debug function for production
  static globalLogout() {
    try {
      // Create new instance for static method since we can't access the singleton from static context
      const instance = new UserService()
      instance.clearLocalSession()
      instance.forceLogout()
    } catch (error) {
      console.error('Global logout failed:', error)
      window.location.replace('/')
    }
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.currentUser && !!this.sessionToken
  }

  // Get user's remaining usage
  getRemainingUsage() {
    if (!this.currentUser) return 0
    return Math.max(0, this.currentUser.usage_limit - this.currentUser.usage_count)
  }

  // Check if user has premium features (considering subscription expiry)
  hasPremiumFeatures() {
    if (!this.currentUser || this.currentUser.subscription_tier === 'free') return false
    
    // Check subscription status
    if (this.currentUser.subscription_status !== 'active') return false
    
    // Check expiry date  
    if (this.currentUser.subscription_end_date) {
      const isNotExpired = new Date(this.currentUser.subscription_end_date) > new Date()
      if (!isNotExpired) {
        // Subscription expired, handle downgrade
        this.handleExpiredSubscriptionSync()
        return false
      }
      return true
    }
    
    return true
  }

  // Handle expired subscription synchronously (for immediate effect)
  handleExpiredSubscriptionSync() {
    if (!this.currentUser || this.currentUser.subscription_tier === 'free') return

    console.log('⚠️ Subscription expired, downgrading to free tier immediately')
    
    // Update local user data immediately
    this.currentUser.subscription_tier = 'free'
    this.currentUser.subscription_status = 'expired'
    this.currentUser.usage_limit = 3 // Free tier limit
    localStorage.setItem('logislip_user', JSON.stringify(this.currentUser))

    // Dispatch event for UI updates
    window.dispatchEvent(new CustomEvent('subscriptionExpired', {
      detail: {
        message: 'Your subscription has expired. You\'ve been moved to the free plan. Upgrade to continue using premium features.',
        previousTier: this.currentUser.subscription_tier
      }
    }))

    // Async update to database (don't block UI)
    this.updateSubscriptionToFree()
  }

  // Update subscription to free tier in database (async)
  async updateSubscriptionToFree() {
    try {
      await this.updateSubscription({
        tier: 'free',
        status: 'expired',
        endDate: null,
        usageLimit: 3
      })
    } catch (error) {
      console.error('Error updating expired subscription in database:', error)
    }
  }

  // Regenerate user avatar - creates a new random avatar
  async regenerateUserAvatar() {
    if (!this.currentUser) {
      throw new Error('No user logged in');
    }

    try {
      // Generate new random avatar - force regeneration
      const newAvatarUrl = this.getUserAvatar(this.currentUser.email, true);

      // Check if Supabase is configured
      if (!process.env.REACT_APP_SUPABASE_URL ||
        process.env.REACT_APP_SUPABASE_URL === 'https://your-project.supabase.co' ||
        !process.env.REACT_APP_SUPABASE_ANON_KEY ||
        process.env.REACT_APP_SUPABASE_ANON_KEY === 'your-supabase-anon-key') {
        // Update local storage in fallback mode
        this.currentUser.avatar_url = newAvatarUrl;
        localStorage.setItem('logislip_user', JSON.stringify(this.currentUser));
        return this.currentUser;
      }

      // Update in database
      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({ avatar_url: newAvatarUrl })
        .eq('id', this.currentUser.id)
        .select()
        .single();

      if (error) throw error;

      // Update local user data
      this.currentUser = updatedUser;
      localStorage.setItem('logislip_user', JSON.stringify(this.currentUser));

      // Log the action
      await this.logUserAction('avatar_regenerated', {
        new_avatar_url: newAvatarUrl
      }, 'user_profile', this.currentUser.id);

      return updatedUser;
    } catch (error) {
      secureLogger.error('Error regenerating avatar:', error);
      throw error;
    }
  }

  // Initialize user avatar if missing
  initializeAvatar() {
    if (this.currentUser && !this.currentUser.avatar_url) {
      this.currentUser.avatar_url = this.generateAvatarUrl(this.currentUser.email);
      localStorage.setItem('logislip_current_user', JSON.stringify(this.currentUser));
    }
  }

  // Generate avatar URL based on email
  generateAvatarUrl(email, forceRegenerate = false) {
    const storedKey = `avatar_${email}`;
    const storedAvatar = localStorage.getItem(storedKey);

    if (storedAvatar && !forceRegenerate) {
      return storedAvatar;
    }

    // Generate using DiceBear API
    const seed = encodeURIComponent(email);
    const style = 'initials'; // You can change this to other styles like 'avataaars', 'bottts', etc.
    const newAvatar = `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&backgroundColor=random`;

    // Store avatar URL
    localStorage.setItem(storedKey, newAvatar);

    return newAvatar;
  }
}

// Create singleton instance
const userService = new UserService()

// Make global logout available for production debugging
if (typeof window !== 'undefined') {
  window.logislipForceLogout = () => UserService.globalLogout()
}

export default userService 